import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

const STATUS_OPTIONS = [
  "Prospect/Not Currently Listed",
  "Appraisal",
  "On Market Listing",
  "Off Market Listing",
];

const CLASS_OPTIONS = [
  { label: "Residential", value: "residential" },
  { label: "Rental", value: "rental" },
  { label: "Commercial", value: "commercial" },
  { label: "Business", value: "business" },
  { label: "Land", value: "land" },
  { label: "Rural", value: "rural" },
  { label: "Holiday Rental", value: "holidayRental" },
];

const LISTING_TYPE_OPTIONS = [
  { label: "For Sale", value: "sale" },
  { label: "For Lease / Rental", value: "lease" },
];

const RENTAL_CLASSES = new Set(["rental", "holidayrental"]);

const inferListingTypeFromProperty = (property) => {
  const explicit = (property?.transaction_type || "").toLowerCase();
  if (explicit === "sale" || explicit === "lease") {
    return explicit === "sale" ? "lease" : "sale";
  }
  const raw = (
    property?.listing_type ||
    property?.property_type ||
    property?.property_class ||
    ""
  ).toLowerCase();
  if (RENTAL_CLASSES.has(raw)) return "sale";
  if (property?.weekly_rent != null && property?.weekly_rent !== "") return "sale";
  return "lease";
};

const getSourceAddress = (property) => {
  if (!property) return "";
  if (property.address) return property.address;
  const parts = [
    property.sub_number ? `${property.sub_number}/` : "",
    property.street_number,
    property.street,
    property.suburb,
  ]
    .filter(Boolean)
    .join(" ");
  return parts.trim();
};

const inferClassFromProperty = (property) => {
  const raw = (
    property?.listing_type ||
    property?.property_type ||
    ""
  ).toLowerCase();
  // Default behaviour: switch rentals <-> sales
  if (raw === "rental" || raw === "holidayrental" || raw === "holidayrental")
    return "residential";
  if (raw === "residential") return "rental";
  if (CLASS_OPTIONS.some((o) => o.value.toLowerCase() === raw))
    return CLASS_OPTIONS.find((o) => o.value.toLowerCase() === raw).value;
  return "residential";
};

const classForListingType = (currentClass, listingType) => {
  const cls = (currentClass || "").toLowerCase();
  if (listingType === "lease") {
    if (RENTAL_CLASSES.has(cls)) return currentClass;
    return "rental";
  }
  // listingType === "sale": if class is a rental flavour, switch back to residential
  if (RENTAL_CLASSES.has(cls)) return "residential";
  return currentClass || "residential";
};

export default function DuplicatePropertyModal({
  isOpen,
  onClose,
  onSubmit,
  property,
  loading,
}) {
  const sourceAddress = useMemo(() => getSourceAddress(property), [property]);

  // All statuses are always selectable — including the property's current one,
  // e.g. duplicating an On Market Listing into another On Market Listing (splitting
  // a listing) is a legitimate use case that a "hide the current status" filter
  // used to block.
  const availableStatuses = STATUS_OPTIONS;

  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [klass, setKlass] = useState("residential");
  const [listingType, setListingType] = useState("sale");
  const [weeklyRent, setWeeklyRent] = useState("");
  const [subNumber, setSubNumber] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [street, setStreet] = useState("");
  const [copyPhotos, setCopyPhotos] = useState(true);
  const [copyOwners, setCopyOwners] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStatus(availableStatuses[0] || STATUS_OPTIONS[0]);
    const inferredClass = inferClassFromProperty(property);
    const inferredListingType = inferListingTypeFromProperty(property);
    setKlass(classForListingType(inferredClass, inferredListingType));
    setListingType(inferredListingType);
    setWeeklyRent(
      property?.weekly_rent != null && property?.weekly_rent !== ""
        ? String(property.weekly_rent)
        : "",
    );
    setSubNumber(property?.sub_number || "");
    setStreetNumber(property?.street_number || "");
    setStreet(property?.street || "");
    setCopyPhotos(true);
    setCopyOwners(false);
  }, [isOpen, property]);

  const handleListingTypeChange = (next) => {
    setListingType(next);
    setKlass((prev) => classForListingType(prev, next));
    if (next === "sale") setWeeklyRent("");
  };

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const cleanedRent = String(weeklyRent || "").replace(/[$,\s]/g, "");
    const rentValue =
      listingType === "lease" && cleanedRent !== "" && !Number.isNaN(Number(cleanedRent))
        ? cleanedRent
        : "";
    onSubmit({
      status,
      property_class: klass,
      transaction_type: listingType,
      weekly_rent: rentValue,
      sub_number: subNumber,
      street_number: streetNumber,
      street,
      copy_photos: copyPhotos,
      copy_owners: copyOwners,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-md bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between bg-[#3F7AAE] text-white px-4 py-3">
          <h3 className="text-sm font-semibold">Property: Create A Copy</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          <div className="flex items-center gap-3">
            <label className="w-20 text-[13px] text-gray-700">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="flex-1 h-9 rounded border border-gray-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
            >
              {availableStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <label className="w-20 text-[13px] text-gray-700">Source</label>
            <div className="flex-1 text-[13px] text-gray-800">
              {sourceAddress || "—"}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <label className="w-20 text-[13px] text-gray-700 pt-2">Listing</label>
            <div className="flex-1 space-y-2">
              <div className="flex rounded border border-gray-300 overflow-hidden">
                {LISTING_TYPE_OPTIONS.map((opt) => {
                  const active = listingType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleListingTypeChange(opt.value)}
                      className={`flex-1 py-2 px-3 text-[13px] font-medium transition-colors ${
                        active
                          ? "bg-[#3F7AAE] text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      aria-pressed={active}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {listingType === "lease" && (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-gray-700">Weekly Rent</span>
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                      $
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={weeklyRent}
                      onChange={(e) => setWeeklyRent(e.target.value)}
                      placeholder="e.g. 650"
                      className="w-full h-9 rounded border border-gray-300 pl-6 pr-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="w-20 text-[13px] text-gray-700">Class</label>
            <select
              value={klass}
              onChange={(e) => setKlass(e.target.value)}
              className="flex-1 h-9 rounded border border-gray-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
            >
              {CLASS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <label className="w-20 text-[13px] text-gray-700">Address</label>
            <div className="flex-1 grid grid-cols-3 gap-2">
              <input
                type="text"
                value={subNumber}
                onChange={(e) => setSubNumber(e.target.value)}
                placeholder="Unit/Lot"
                className="h-9 rounded border border-gray-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
              />
              <input
                type="text"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                placeholder="Number"
                className="h-9 rounded border border-gray-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
              />
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Street"
                className="h-9 rounded border border-gray-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
              />
            </div>
          </div>

          <div className="flex items-center gap-6 pt-1 pl-[5.5rem]">
            <label className="flex items-center gap-2 text-[13px] text-gray-700">
              <input
                type="checkbox"
                checked={copyPhotos}
                onChange={(e) => setCopyPhotos(e.target.checked)}
                className="h-4 w-4 accent-[#55C5D0]"
              />
              Copy Property Photos
            </label>
            <label className="flex items-center gap-2 text-[13px] text-gray-700">
              <input
                type="checkbox"
                checked={copyOwners}
                onChange={(e) => setCopyOwners(e.target.checked)}
                className="h-4 w-4 accent-[#55C5D0]"
              />
              Copy Owner(s)
            </label>
          </div>

          <div className="flex items-center gap-3 pl-[5.5rem] pt-2">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded bg-[#1ABC9C] hover:bg-[#17A589] disabled:opacity-60 disabled:cursor-not-allowed text-white text-[13px] font-semibold"
            >
              {loading ? "Creating..." : "Create a Copy"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-[13px] font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
